/**
 * React Query hooks for the Advertising Optimization arche. All server
 * state flows through these (React Query v5 conventions per
 * docs/react-query-patterns.md); mutations invalidate the narrowest
 * relevant key.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type AdoptCreateCampaignRequest,
  type AdoptCreateCreativeRequest,
  type AdoptCreateExperimentRequest,
  type AdoptCreateLinkRequest,
  type AdoptCreatePlacementRequest,
  type AdoptFunnelGoalInput,
  type AdoptPutSettingsRequest,
  type AdoptRsaContentDto,
  type AdoptUpdateCampaignRequest,
} from '@/lib/api';
import { adoptQueryKeys } from '../api/queryKeys';

// =============================================================================
// Campaigns
// =============================================================================

export function useAdoptCampaigns(
  holderId: string | null,
  filters: { status?: string; promotedArcheId?: string } = {},
) {
  return useQuery({
    queryKey: adoptQueryKeys.campaignsList(holderId ?? '', filters),
    queryFn: () => api.adoptListCampaigns(holderId!, filters),
    enabled: !!holderId,
    select: (data) => data.campaigns,
  });
}

export function useAdoptCampaign(holderId: string | null, campaignId: string | undefined) {
  return useQuery({
    queryKey: adoptQueryKeys.campaign(holderId ?? '', campaignId ?? ''),
    queryFn: () => api.adoptGetCampaign(holderId!, campaignId!),
    enabled: !!holderId && !!campaignId,
  });
}

export function useCreateAdoptCampaign(holderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdoptCreateCampaignRequest) => api.adoptCreateCampaign(holderId!, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.campaigns(holderId ?? '') });
    },
  });
}

export function useUpdateAdoptCampaign(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdoptUpdateCampaignRequest) =>
      api.adoptUpdateCampaign(holderId!, campaignId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.campaigns(holderId ?? '') });
    },
  });
}

export function useAdoptCampaignLifecycle(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (verb: 'activate' | 'pause' | 'complete' | 'archive') =>
      api.adoptCampaignLifecycle(holderId!, campaignId, verb),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.campaigns(holderId ?? '') });
    },
  });
}

export function useAdoptKillSwitch(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => api.adoptSetKillSwitch(holderId!, campaignId, enabled),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.campaigns(holderId ?? '') });
    },
  });
}

export function usePutAdoptFunnelGoals(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (goals: AdoptFunnelGoalInput[]) =>
      api.adoptPutFunnelGoals(holderId!, campaignId, goals),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.campaign(holderId ?? '', campaignId),
      });
    },
  });
}

export function usePutAdoptStrategy(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (strategy: Record<string, number>) =>
      api.adoptPutStrategy(holderId!, campaignId, strategy),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.campaigns(holderId ?? '') });
    },
  });
}

// =============================================================================
// Settings
// =============================================================================

export function useAdoptSettings(holderId: string | null) {
  return useQuery({
    queryKey: adoptQueryKeys.settings(holderId ?? ''),
    queryFn: () => api.adoptGetSettings(holderId!),
    enabled: !!holderId,
    select: (data) => data.settings,
  });
}

export function usePutAdoptSettings(holderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdoptPutSettingsRequest) => api.adoptPutSettings(holderId!, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.settings(holderId ?? '') });
    },
  });
}

// =============================================================================
// Tracked links
// =============================================================================

export function useAdoptLinks(holderId: string | null) {
  return useQuery({
    queryKey: adoptQueryKeys.links(holderId ?? ''),
    queryFn: () => api.adoptListLinks(holderId!, { status: 'active' }),
    enabled: !!holderId,
    select: (data) => data.links,
  });
}

export function useCreateAdoptLink(holderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdoptCreateLinkRequest) => api.adoptCreateLink(holderId!, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.links(holderId ?? '') });
    },
  });
}

export function useArchiveAdoptLink(holderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => api.adoptArchiveLink(holderId!, linkId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.links(holderId ?? '') });
    },
  });
}

// =============================================================================
// Goals
// =============================================================================

export function useAdoptGoals(holderId: string | null) {
  return useQuery({
    queryKey: [...adoptQueryKeys.all, 'goals', holderId ?? ''],
    queryFn: () => api.adoptListGoals(holderId!),
    enabled: !!holderId,
    select: (data) => data.goals,
  });
}

// =============================================================================
// Channel accounts + placements
// =============================================================================

export function useAdoptAccounts(holderId: string | null) {
  return useQuery({
    queryKey: adoptQueryKeys.accounts(holderId ?? ''),
    queryFn: () => api.adoptListAccounts(holderId!),
    enabled: !!holderId,
    select: (data) => data.accounts,
  });
}

export function useAdoptPlacements(holderId: string | null, campaignId: string | undefined) {
  return useQuery({
    queryKey: adoptQueryKeys.placements(holderId ?? '', campaignId ?? ''),
    queryFn: () => api.adoptListPlacements(holderId!, campaignId!),
    enabled: !!holderId && !!campaignId,
    select: (data) => data.placements,
  });
}

export function useCreateAdoptPlacement(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdoptCreatePlacementRequest) =>
      api.adoptCreatePlacement(holderId!, campaignId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.placements(holderId ?? '', campaignId),
      });
    },
  });
}

export function useDeployAdoptPlacement(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (placementId: string) => api.adoptDeployPlacement(holderId!, placementId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.placements(holderId ?? '', campaignId),
      });
    },
  });
}

export function useAdoptPlacementLifecycle(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ placementId, verb }: { placementId: string; verb: 'pause' | 'resume' }) =>
      api.adoptPlacementLifecycle(holderId!, placementId, verb),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.placements(holderId ?? '', campaignId),
      });
    },
  });
}

// =============================================================================
// Creatives + generation
// =============================================================================

export function useAdoptCreatives(
  holderId: string | null,
  campaignId: string | undefined,
  filters: { status?: string } = {},
) {
  return useQuery({
    queryKey: [...adoptQueryKeys.creatives(holderId ?? '', campaignId ?? ''), filters],
    queryFn: () => api.adoptListCreatives(holderId!, campaignId!, filters),
    enabled: !!holderId && !!campaignId,
    select: (data) => data.creatives,
  });
}

export function useCreateAdoptCreative(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdoptCreateCreativeRequest) =>
      api.adoptCreateCreative(holderId!, campaignId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.creatives(holderId ?? '', campaignId),
      });
    },
  });
}

export function useUpdateAdoptCreative(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ creativeId, content }: { creativeId: string; content: AdoptRsaContentDto }) =>
      api.adoptUpdateCreative(holderId!, creativeId, content),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.creatives(holderId ?? '', campaignId),
      });
    },
  });
}

export function useAdoptCreativeAction(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      creativeId,
      verb,
    }: {
      creativeId: string;
      verb: 'approve' | 'reject' | 'pause' | 'resume';
    }) => api.adoptCreativeAction(holderId!, creativeId, verb),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.creatives(holderId ?? '', campaignId),
      });
    },
  });
}

export function useDeployAdoptCreative(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      creativeId,
      placementId,
      experimentId,
      variantKey,
    }: {
      creativeId: string;
      placementId?: string;
      experimentId?: string;
      variantKey?: string;
    }) => api.adoptDeployCreative(holderId!, creativeId, { placementId, experimentId, variantKey }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.creatives(holderId ?? '', campaignId),
      });
    },
  });
}

export function useKillAdoptCreative(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ creativeId, reason }: { creativeId: string; reason: string }) =>
      api.adoptKillCreative(holderId!, creativeId, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.creatives(holderId ?? '', campaignId),
      });
    },
  });
}

export function useGenerateAdoptCopy(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      count?: number;
      angles?: string[];
      instructions?: string;
      placementId?: string;
    }) => api.adoptGenerateCopy(holderId!, campaignId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.creatives(holderId ?? '', campaignId),
      });
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.generations(holderId ?? '', campaignId),
      });
    },
  });
}

export function useGenerateAdoptChallengers(holderId: string | null, campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { count?: number; placementId?: string }) =>
      api.adoptGenerateChallengers(holderId!, campaignId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.creatives(holderId ?? '', campaignId),
      });
      void queryClient.invalidateQueries({
        queryKey: adoptQueryKeys.generations(holderId ?? '', campaignId),
      });
    },
  });
}

export function useGenerateAdoptKeywords(holderId: string | null, campaignId: string) {
  return useMutation({
    mutationFn: (body: { seedTerms?: string[]; count?: number }) =>
      api.adoptGenerateKeywords(holderId!, campaignId, body),
  });
}

export function useAdoptGenerations(holderId: string | null, campaignId: string | undefined) {
  return useQuery({
    queryKey: adoptQueryKeys.generations(holderId ?? '', campaignId ?? ''),
    queryFn: () => api.adoptListGenerations(holderId!, campaignId!),
    enabled: !!holderId && !!campaignId,
    select: (data) => data.generations,
  });
}

// =============================================================================
// Action Inbox
// =============================================================================

export function useAdoptActions(
  holderId: string | null,
  filters: { status?: string; campaignId?: string } = {},
) {
  return useQuery({
    queryKey: adoptQueryKeys.actionsList(holderId ?? '', filters),
    queryFn: () => api.adoptListActions(holderId!, filters),
    enabled: !!holderId,
    select: (data) => data.actions,
    // Proposals arrive on the hourly optimize sweep; poll lazily.
    refetchInterval: 60_000,
  });
}

export function useReviewAdoptAction(holderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ actionId, verb }: { actionId: string; verb: 'approve' | 'reject' }) =>
      api.adoptReviewAction(holderId!, actionId, verb),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.actions(holderId ?? '') });
      // Approvals mutate campaigns/placements/creatives downstream.
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.campaigns(holderId ?? '') });
    },
  });
}

export function useAdoptAutoUnlock(holderId: string | null) {
  return useQuery({
    queryKey: adoptQueryKeys.autoUnlock(holderId ?? ''),
    queryFn: () => api.adoptGetAutoUnlock(holderId!),
    enabled: !!holderId,
    select: (data) => data.autoUnlock,
  });
}

// =============================================================================
// Experiments
// =============================================================================

export function useAdoptExperiments(holderId: string | null) {
  return useQuery({
    queryKey: adoptQueryKeys.experiments(holderId ?? ''),
    queryFn: () => api.adoptListExperiments(holderId!),
    enabled: !!holderId,
    select: (data) => data.experiments,
  });
}

export function useAdoptExperiment(holderId: string | null, experimentId: string | undefined) {
  return useQuery({
    queryKey: adoptQueryKeys.experiment(holderId ?? '', experimentId ?? ''),
    queryFn: () => api.adoptGetExperiment(holderId!, experimentId!),
    enabled: !!holderId && !!experimentId,
  });
}

export function useAdoptExperimentResults(
  holderId: string | null,
  experimentId: string | undefined,
) {
  return useQuery({
    queryKey: adoptQueryKeys.experimentResults(holderId ?? '', experimentId ?? ''),
    queryFn: () => api.adoptGetExperimentResults(holderId!, experimentId!),
    enabled: !!holderId && !!experimentId,
    // Posteriors move on the hourly stats sweep; poll lazily.
    refetchInterval: 60_000,
  });
}

export function useCreateAdoptExperiment(holderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdoptCreateExperimentRequest) => api.adoptCreateExperiment(holderId!, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.experiments(holderId ?? '') });
    },
  });
}

export function useAdoptExperimentLifecycle(holderId: string | null, experimentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (verb: 'start' | 'stop' | 'complete' | 'archive') =>
      api.adoptExperimentLifecycle(holderId!, experimentId, verb),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adoptQueryKeys.experiments(holderId ?? '') });
    },
  });
}
